import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';

export default function ApplicationMain() {
  const location = useLocation();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    // Get customer ID from URL query params
    const params = new URLSearchParams(location.search);
    const id = params.get('customerid');
    
    if (!id) {
      // If no customer ID, redirect to home
      navigate('/');
    } else {
      setCustomerId(id);
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen" data-testid="application-main-page">
      <Helmet>
        <title>Application Submitted Successfully - Trademark Venus</title>
        <meta name="description" content="Your trademark application has been successfully submitted. Our team will contact you shortly." />
      </Helmet>
      
      {/* Success Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-95" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-green-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white leading-tight mb-6">
              Application Submitted Successfully!
            </h1>
            
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              Thank you for choosing Trademark Venus. Your application has been received and is being processed.
            </p>
            
            {customerId && (
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 inline-block">
                <p className="text-sm text-slate-400 mb-2">Your Customer ID</p>
                <p className="text-2xl font-bold text-white font-mono">{customerId}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <p className="text-slate-300">
                Our team will review your application and contact you within 24-48 hours.
              </p>
              <p className="text-slate-300">
                Please check your email for confirmation and next steps.
              </p>
            </div>
            
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300"
              >
                Return to Home
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Next Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
                What Happens Next?
              </h2>
              <p className="text-slate-600 text-lg">
                Here's what you can expect in the coming days
              </p>
            </motion.div>

            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Application Review',
                  description: 'Our expert team will review your application details and verify all information.',
                  time: 'Within 24 hours'
                },
                {
                  step: '2',
                  title: 'Confirmation Email',
                  description: 'You\'ll receive a confirmation email with your case details and assigned attorney information.',
                  time: '24-48 hours'
                },
                {
                  step: '3',
                  title: 'Initial Consultation',
                  description: 'One of our trademark attorneys will reach out to discuss your case and answer any questions.',
                  time: '2-3 business days'
                },
                {
                  step: '4',
                  title: 'Filing Process Begins',
                  description: 'Once approved, we\'ll begin the trademark filing process with the USPTO.',
                  time: '3-5 business days'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-6 bg-gradient-to-r from-slate-50 to-white rounded-2xl p-6 border border-slate-100"
                >
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-red-600 to-red-700 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 mb-2">
                      {item.description}
                    </p>
                    <p className="text-sm text-red-600 font-semibold">
                      {item.time}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Support Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-red-900 via-red-700 to-red-600">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Need Help?
            </h2>
            <p className="text-red-100 text-lg max-w-2xl mx-auto mb-8">
              If you have any questions or concerns, our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:1-800-862-7263" className="text-white hover:text-red-100 transition-colors">
                <Button className="bg-white text-red-700 hover:bg-red-50 font-semibold rounded-full px-8 py-6 text-lg">
                  Call: +1 (888) 271-4168
                </Button>
              </a>
              <a href="mailto:info@trademarkvenus.com" className="text-white hover:text-red-100 transition-colors">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold rounded-full px-8 py-6 text-lg">
                  Email Us
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
